import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { PageHeader, Panel } from "../components/UI";
import "./Dashboard.css";

const API = "";

const playerTimeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Phoenix", month: "short", day: "numeric", year: "numeric",
  hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true
});

function formatPlayerTime(epoch) {
  if (epoch == null) return "—";
  const parts = Object.fromEntries(playerTimeFormatter.formatToParts(new Date(epoch))
    .map(({ type, value }) => [type, value]));
  return `${parts.month} ${parts.day} ${parts.year} ${parts.hour}:${parts.minute}:${parts.second} ${parts.dayPeriod} MST`;
}

export default function Dashboard() {

  const [status, setStatus] = useState("unknown");
  const [metrics, setMetrics] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const [metricsOpen, setMetricsOpen] = useState(() => {
    const saved = localStorage.getItem("metricsOpen");
    return saved === null ? true : saved === "true";
  });

  const [logsOpen, setLogsOpen] = useState(() => {
    const saved = localStorage.getItem("logsOpen");
    return saved === null ? false : saved === "true";
  });

  const [history, setHistory] = useState({
    cpu: [],
    memory: [],
    disk: [],
    rx: [],
    tx: []
  });

  const [logs, setLogs] = useState([]);
  const logRef = useRef(null);

  // ----- Active Players -----
  const [players, setPlayers] = useState([]);
  const [playersOpen, setPlayersOpen] = useState(() => {
    const saved = localStorage.getItem("playersOpen");
    return saved === null ? false : saved === "true";
  });

  useEffect(() => {
    localStorage.setItem("metricsOpen", metricsOpen);
  }, [metricsOpen]);

  useEffect(() => {
    localStorage.setItem("logsOpen", logsOpen);
  }, [logsOpen]);

useEffect(() => {
  localStorage.setItem("playersOpen", playersOpen);
}, [playersOpen]);

  const fetchData = () => {
    axios.get(`${API}/api/server/status`)
      .then(res => setStatus(res.data.status));

    axios.get(`${API}/api/metrics`)
      .then(res => {
        setMetrics(res.data);

        setHistory(prev => ({
          cpu: [...prev.cpu.slice(-29), parseFloat(res.data.cpu)],
          memory: [...prev.memory.slice(-29), parseFloat(res.data.memory.percent)],
          disk: [...prev.disk.slice(-29), parseFloat(res.data.disk.percent)],
          rx: [...prev.rx.slice(-29), res.data.network.rxRate],
          tx: [...prev.tx.slice(-29), res.data.network.txRate]
        }));
      });
  };

  const fetchPlayers = () => {
    axios.get(`${API}/api/players`)
      .then(res => setPlayers(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    fetchData();
    fetchPlayers();
    const interval = setInterval(fetchData, 3000);
    const playersInterval = setInterval(fetchPlayers, 5000);

    const eventSource = new EventSource(`${API}/api/logs`);
    eventSource.onmessage = e => {
      setLogs(prev => [...prev, e.data]);
    };

    return () => {
      clearInterval(interval);
      clearInterval(playersInterval);
      eventSource.close();
    };
  }, []);

  useEffect(() => {
    if (logRef.current && logsOpen) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs, logsOpen]);

  const startServer = () =>
    axios.post(`${API}/api/server/start`).then(fetchData);

  const stopServer = () =>
    axios.post(`${API}/api/server/stop`).then(fetchData);

  const formatGB = bytes =>
    (bytes / 1024 / 1024 / 1024).toFixed(2) + " GB";

  const thresholdColor = percent => {
    if (percent < 60) return "#3ccf91";
    if (percent < 85) return "#f4c430";
    return "#ff4c4c";
  };

  const Meter = ({ label, percent, sub }) => (
    <div className="metric-meter">
      <div className="metric-label"><span>{label}</span><strong>{percent}%</strong></div>
      <div className="meter-track">
        <div style={{ width: `${percent}%`, background: thresholdColor(percent) }} />
      </div>
      <div className="metric-sub">{sub}</div>
    </div>
  );

  const MultiLineChart = ({ history }) => {

    const height = 150;
    const width = 1000;
    const maxPoints = history.cpu.length;
    if (maxPoints < 2) return null;

    const allData = [
      ...history.cpu,
      ...history.memory,
      ...history.disk,
      ...history.rx,
      ...history.tx
    ];

    const max = Math.max(...allData);
    const min = Math.min(...allData);
    const range = max - min || 1;

    const buildPath = (data) => {
      const points = data.map((v, i) => ({
        x: (i / (data.length - 1)) * width,
        y: height - ((v - min) / range) * height
      }));

      let d = `M ${points[0].x} ${points[0].y}`;
      for (let i = 0; i < points.length - 1; i++) {
        const cpX = (points[i].x + points[i + 1].x) / 2;
        d += ` C ${cpX} ${points[i].y}, ${cpX} ${points[i + 1].y}, ${points[i + 1].x} ${points[i + 1].y}`;
      }
      return d;
    };

    const handleMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const index = Math.round((x / rect.width) * (maxPoints - 1));
      setHoverIndex(index);
    };

    const handleMouseLeave = () => setHoverIndex(null);

    const getValue = arr =>
      hoverIndex !== null ? arr[hoverIndex]?.toFixed(2) : null;

    const timeLabels = Array.from({ length: maxPoints }, (_, i) =>
      `${(maxPoints - i - 1) * 3}s`
    );

    return (
      <div>
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >

          {/* Horizontal Grid Lines */}
          {[0.25, 0.5, 0.75].map((p, i) => (
            <line
              key={i}
              x1="0"
              x2={width}
              y1={height * p}
              y2={height * p}
              stroke="#2a2f3a"
              strokeWidth="1"
            />
          ))}

          <path d={buildPath(history.cpu)} stroke="#3ccf91" fill="none" strokeWidth="2" />
          <path d={buildPath(history.memory)} stroke="#f4c430" fill="none" strokeWidth="2" />
          <path d={buildPath(history.disk)} stroke="#ff4c4c" fill="none" strokeWidth="2" />
          <path d={buildPath(history.rx)} stroke="#4da6ff" fill="none" strokeWidth="1.5" />
          <path d={buildPath(history.tx)} stroke="#c084fc" fill="none" strokeWidth="1.5" />

          {hoverIndex !== null && (
            <line
              x1={(hoverIndex / (maxPoints - 1)) * width}
              x2={(hoverIndex / (maxPoints - 1)) * width}
              y1="0"
              y2={height}
              stroke="#888"
              strokeDasharray="4"
            />
          )}
        </svg>

        <div className="chart-legend">
          <span style={{ color:"#3ccf91" }}>CPU {getValue(history.cpu)}</span>
          <span style={{ color:"#f4c430" }}>Memory {getValue(history.memory)}</span>
          <span style={{ color:"#ff4c4c" }}>Disk {getValue(history.disk)}</span>
          <span style={{ color:"#4da6ff" }}>RX {getValue(history.rx)}</span>
          <span style={{ color:"#c084fc" }}>TX {getValue(history.tx)}</span>
        </div>

        <div style={{
          display:"flex",
          justifyContent:"space-between",
          fontSize:10,
          opacity:.6,
          marginTop:4
        }}>
          <span>{timeLabels[0]}</span>
          <span>{timeLabels[Math.floor(maxPoints/2)]}</span>
          <span>Now</span>
        </div>
      </div>
    );
  };

  const connectedPlayerCount = players.filter(player => player.status !== "Disconnected").length;
  const sortedPlayers = [...players].sort((a, b) =>
    Number(a.status === "Disconnected") - Number(b.status === "Disconnected"));

  return (
    <div className="page-stack dashboard">
      <PageHeader title="Dashboard" description="Server health, performance and live activity." />
      <div className="dashboard-top-grid">
        <Panel title="Server Status" className="dashboard-status">
          <div className={`status-badge ${status === "active" ? "is-active" : "is-inactive"}`}>
            <span className="status-dot" />{status}
          </div>
          <h3>Reforger Game Server</h3>
          <p className="muted">Arma Reforger · Dedicated server</p>
          <p className="dashboard-caption">Server Overview</p>
        </Panel>
        <Panel title="Quick Actions" className="dashboard-actions">
          <p className="muted">Manage the game server</p>
          <div className="button-row">
            <button className="success" onClick={startServer}>Start</button>
            <button className="danger" onClick={stopServer}>Stop</button>
          </div>
        </Panel>
        <section className="panel dashboard-players" aria-labelledby="live-players-title">
          <div className="panel-header">
            <h2 id="live-players-title">Live Players</h2>
            <span className="dashboard-player-count">{connectedPlayerCount}<span> connected</span></span>
          </div>
          <Panel title={`Active Players (${connectedPlayerCount})`} open={playersOpen}
            onToggle={() => setPlayersOpen(!playersOpen)} id="players-content">
            {connectedPlayerCount === 0 && <div className="empty-state">No players connected</div>}
            <div className="player-list">
              {sortedPlayers.map(player => (
                <div key={player.playerId} className="player-row">
                  <div className="player-name" style={{
                    color: player.status === "Disconnected" ? "#93a1b1" : Date.now() - player.connectedAt < 60000
                      ? "#4da6ff"   // recently joined (blue)
                      : "#3ccf91"   // stable (green)
                  }}>{player.name}</div>
                  <div className="player-identity">ID: {player.identityId || "Pending"}</div>
                  <div className="player-details">
                    <span>Status: {player.status || "Connected"}</span>
                    <span>Connected: {formatPlayerTime(player.connectedAt)}</span>
                    <span>Last Seen: {formatPlayerTime(player.lastSeen)}</span>
                    <span>Session Length: {player.sessionLength ?? Math.max(0, Math.floor(((player.disconnectedAt ?? Date.now()) - player.connectedAt) / 1000))}s</span>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </section>
      </div>

      <Panel title="Game Server Performance Metrics" open={metricsOpen}
        onToggle={() => setMetricsOpen(!metricsOpen)} id="performance-content" className="dashboard-performance">
        {metrics && (
          <>
            <div className="metric-grid">
              <Meter label="CPU" percent={metrics.cpu} sub="Load Average Based" />
              <Meter label="Memory" percent={metrics.memory.percent}
                sub={`${formatGB(metrics.memory.used)} / ${formatGB(metrics.memory.total)}`} />
              <Meter label="Disk" percent={metrics.disk.percent}
                sub={`${formatGB(metrics.disk.used)} / ${formatGB(metrics.disk.total)}`} />
              <div className="metric-meter dashboard-network">
                <div className="metric-label"><span>Network</span></div>
                <div className="dashboard-network-values">
                  <div><span>↓ RX</span><strong>{metrics.network.rxRate}<small> B</small></strong></div>
                  <div><span>↑ TX</span><strong>{metrics.network.txRate}<small> B</small></strong></div>
                </div>
                <div className="metric-sub">Traffic since previous sample</div>
              </div>
            </div>
            <div className="chart-area">
              <h2>Performance History</h2>
              <MultiLineChart history={history} />
            </div>
          </>
        )}
      </Panel>
      <Panel title="Live Logs" open={logsOpen} onToggle={() => setLogsOpen(!logsOpen)} id="logs-content" className="logs-panel">
        <div className="dashboard-terminal-bar"><span aria-hidden="true">›_</span> Server output</div>
        <div ref={logRef} className="log-console" role="region" aria-label="Server log output" tabIndex={0}>
          {logs.map((line, i) => <div key={i}>{line}</div>)}
        </div>
      </Panel>
    </div>
  );
}
